/**
 * Natural sort function for handling numeric parts in strings.
 * This ensures that strings like "Song 10" will appear after "Song 9" in sorting,
 * and files without numbers come before files with numbers when they share the same prefix.
 * 
 * Uses the browser's built-in Intl.Collator which provides the same natural sorting
 * that operating systems like macOS Finder use.
 */
export const naturalSortStrings = (a: string, b: string): number => {
  // Remove file extensions for comparison
  const stripExt = (s: string) => s.replace(/\.[^/.]+$/, '');
  const baseA = stripExt(a);
  const baseB = stripExt(b);

  // If one is a prefix of the other, shorter comes first
  if (baseA !== baseB) {
    if (baseA.startsWith(baseB)) return 1;
    if (baseB.startsWith(baseA)) return -1;
  }

  // Fallback to original logic
  const match = findCommonPrefixWithNumberSuffix(a, b);
  if (match) {
    const { hasNumberA, hasNumberB } = match;
    if (hasNumberA && !hasNumberB) return 1;
    if (!hasNumberA && hasNumberB) return -1;
  }

  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  return collator.compare(a, b);
};

/**
 * Helper function to detect if two strings share a common prefix and
 * one has a number suffix while the other doesn't
 */
function findCommonPrefixWithNumberSuffix(a: string, b: string) {
  // Try to match strings like "DITR minimal" and "DITR minimal 2"
  const regex = /^(.+?)\s*(\d+)?(\.\w+)?$/;
  
  const matchA = a.match(regex);
  const matchB = b.match(regex);
  
  if (matchA && matchB && matchA[1] === matchB[1]) {
    return {
      prefix: matchA[1],
      hasNumberA: !!matchA[2],
      hasNumberB: !!matchB[2]
    };
  }
  
  return null;
}

/**
 * Simple numeric sort function.
 */
export const naturalSortNumbers = (a: number, b: number): number => {
  return a - b;
}; 